import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { DynamicFormField, FieldType } from "@components/ui/DynamicInput";
import { ADDRESS_DETAILS_FIELDS } from "../../loanApplications/formFieldsConfig/addressDetails";
import { Button } from "@components/shadcn-ui/button";
import { addressService, AddressDetails, AddressResponse } from "../service/addressService";
import { useToast } from "@customHooks/useToast";
import { Loader2, X } from "lucide-react";
import {CITY_MAP } from "../../loanApplications/formFieldsConfig/indiaStatesList";
import {isFieldAsCity } from "@lib/utils";
import { Field } from "features/loanApplications/models";

interface AddressFormProps {
  applicationId: string;
  overrideAddressTypes?: string[];
  leadId: string;
  onAddressAdded: (address: AddressResponse) => void;
  onCancel: () => void;
  initialData?: AddressResponse; // Add initialData prop for edit mode
  isEditMode?: boolean; // Flag to indicate if we're in edit mode
}

export const AddressForm: React.FC<AddressFormProps> = ({
  applicationId,
  overrideAddressTypes = [],
  leadId,
  onAddressAdded,
  onCancel,
  initialData,
  isEditMode = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  if (overrideAddressTypes.length > 0 ) {
    ADDRESS_DETAILS_FIELDS.fields.map((field, index) => {
      if (field.name == "type") {
        ADDRESS_DETAILS_FIELDS.fields[index].options = overrideAddressTypes;
      }
    })
  }
  const form = useForm({
    mode: "all",
    defaultValues: initialData || {
      country: "India" // Set default country to India for new addresses
    }, // Pre-populate form with initialData if provided
  });

  // When initialData changes (e.g., when editing a different address), update the form
  useEffect(() => {
    if (initialData) {
      console.log("Setting form values with:", initialData);
      
      // Create a copy of initialData with postalCode as string for the form
      const formValues = {
        ...initialData,
        // Convert numeric fields to strings for the form
        postalCode: initialData.postalCode !== undefined && initialData.postalCode !== null ? String(initialData.postalCode) : '',
        yearsOfStay: initialData.yearsOfStay !== undefined && initialData.yearsOfStay !== null ? String(initialData.yearsOfStay) : '',
        monthlyRent: initialData.monthlyRent !== undefined && initialData.monthlyRent !== null ? String(initialData.monthlyRent) : ''
      };
      
      console.log("Form values after conversion:", formValues);
      
      // Use type assertion to handle the type mismatch
      form.reset(formValues as any);
    }
  }, [initialData, form]);

  // Debug log to verify props
  useEffect(() => {
    console.log("AddressForm props:", { applicationId, leadId, isEditMode, initialData });
    
    // Log the postalCode specifically to help with debugging
    if (initialData) {
      console.log("Initial postalCode:", initialData.postalCode, "Type:", typeof initialData.postalCode);
    }
  }, [applicationId, leadId, isEditMode, initialData]);

  // Reset the city value 
  const selectedState = form.watch("state");
  useEffect(() => {
    // Reset the city value when state changes
    form.setValue("city", "");
  }, [selectedState, form]);

  const handleSubmit = async (data: any) => {
    // Additional validation to ensure IDs are present and valid
    if (!applicationId || applicationId === "undefined" || applicationId === "null") {
      toast({
        variant: "destructive",
        description: "Application ID is missing or invalid",
      });
      console.error("Invalid Application ID:", applicationId);
      return;
    }

    if (!leadId || leadId === "undefined" || leadId === "null") {
      toast({
        variant: "destructive",
        description: "Lead ID is missing or invalid",
      });
      console.error("Invalid Lead ID:", leadId);
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert postalCode and other numeric fields to numbers
      const addressData: AddressDetails = {
        ...data,
        applicationId,
        leadId,
        id: initialData?.id,
        active: true,
        verified: false,
        postalCode: data.postalCode ? Number(data.postalCode) : 0,
        yearsOfStay: data.yearsOfStay ? Number(data.yearsOfStay) : 0,
        monthlyRent: data.monthlyRent ? Number(data.monthlyRent) : undefined,
      };

      console.log(`${isEditMode ? "Updating" : "Creating"} address data:`, addressData);

      let response;
      if (isEditMode && initialData?.id) {
        // Update existing address
        response = await addressService.updateAddress(addressData);
        toast({
          description: "Address updated successfully",
        });
      } else {
        // Create new address
        response = await addressService.createAddress(addressData);
        toast({
          description: "Address added successfully",
        });
      }
      
      onAddressAdded(response);
      form.reset();
    } catch (error) {
      console.error(`Error ${isEditMode ? "updating" : "adding"} address:`, error);
      toast({
        variant: "destructive",
        description: `Failed to ${isEditMode ? "update" : "add"} address. Please try again.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border p-4 rounded-md mb-4 bg-gray-50">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">{isEditMode ? "Edit" : "Add New"} Address</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onCancel}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <FormProvider {...form}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pb-4">
          {ADDRESS_DETAILS_FIELDS.fields.map((field, index) => {
            
            const selectedState = form.watch("state"); 
            const dynamicOptions = isFieldAsCity(field.name) ? CITY_MAP[selectedState]  || [] : field.options || [];
            return (
            <div key={index} className="w-full">
              <DynamicFormField field={{...field, options: dynamicOptions} as FieldType} isDisabled={isSubmitting || (isFieldAsCity(field.name) && !selectedState)} />
            </div> );
          })}
        </div>
        
        <div className="flex justify-end mt-4 gap-2 sticky bottom-0 pt-2 bg-gray-50">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={() => form.handleSubmit(handleSubmit)()}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditMode ? "Updating..." : "Saving..."}
              </>
            ) : (
              isEditMode ? "Update Address" : "Save Address"
            )}
          </Button>
        </div>
      </FormProvider>
    </div>
  );
};
